import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import API from '../utils/apiconfig';
import { useAuth } from '../context/Authcontext';
import { useGlobalError } from '../context/GlobalErrorContext';
import TablePagination from '../components/TablePagination';
import { ScrollableListBody } from '../components/ScrollableTable';
import StatusBadge from '../components/ui/StatusBadge';
import MediaPreviewModal, { handleMediaView } from '../components/MediaPreviewModal';
import { DEFAULT_PAGE_SIZE, parsePaginatedResponse } from '../utils/pagination';
import {
  computeTdsBreakdown,
  computeTdsOnGrossList,
  cycleGross,
  formatInr,
} from '../utils/withdrawalTds';
import AppButton from '../components/ui/AppButton';
import { formatAppDate, formatAppMonth, formatPayoutCutoffDate } from '../utils/dateFormat';
import PayoutWalletNote from '../components/PayoutWalletNote';

const CONCERN_REASONS = [
  { value: 'amount_mismatch', label: 'Amount mismatch' },
  { value: 'incorrect_payment', label: 'Incorrect payment' },
  { value: 'payment_not_received', label: 'Payment not received' },
  { value: 'incorrect_proof', label: 'Incorrect proof' },
  { value: 'other', label: 'Other' },
];

function Withdrawal() {
  const { user } = useAuth();
  const { reportError } = useGlobalError();
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [concernFor, setConcernFor] = useState(null);
  const [concernReason, setConcernReason] = useState('amount_mismatch');
  const [concernRemark, setConcernRemark] = useState('');
  const [reopenFor, setReopenFor] = useState(null);
  const [reopenRemark, setReopenRemark] = useState('');
  const [toast, setToast] = useState('');
  const [media, setMedia] = useState(null);

  const balance = summary?.available_points ?? user?.reward_points ?? 0;
  const totalEarned = summary?.total_earned_points ?? user?.total_earned_points ?? 0;
  const totalWithdrawn = summary?.total_withdrawn_points ?? 0;
  const payoutSaved = Boolean(
    (user?.upi_id || '').trim() || (user?.bank_account_number || '').trim()
  );
  const unpaidStatuses = ['not_started', 'pending', 'approved', 'yet_to_be_paid'];
  const unpaidCycles = withdrawals.filter(
    (w) =>
      ['yet_to_be_paid', 'partially_paid', 'failed', 'processing'].includes(
        w.payment_status || '',
      ) ||
      (unpaidStatuses.includes(w.status) && Number(w.remaining_amount ?? 1) > 0),
  );
  const unpaidConsolidated = computeTdsOnGrossList(unpaidCycles.map(cycleGross));

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const [sumRes, histRes] = await Promise.all([
        api.get(API.WITHDRAWAL_SUMMARY),
        api.get(API.MY_WITHDRAWALS, { params: { page, page_size: pageSize } }),
      ]);
      setSummary(sumRes.data);
      const parsed = parsePaginatedResponse(histRes.data, ['results', 'withdrawals']);
      setWithdrawals(parsed.items);
      setTotalPages(parsed.totalPages);
      setTotalCount(parsed.count);
      setPage(parsed.page);
    } catch (err) {
      reportError(err);
      setWithdrawals([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setHistoryLoading(false);
    }
  }, [page, pageSize, reportError]);

  useEffect(() => {
    if (!user?.id) {
      navigate('/login');
      return;
    }
    loadHistory();
  }, [user?.id, navigate, loadHistory]);

  const readMessage = (payload, fallback) => {
    const msg = payload?.message || payload?.error;
    if (typeof msg === 'string' && msg.trim()) return msg.trim();
    return fallback;
  };

  const acceptPayment = async (w) => {
    setActionId(w.id);
    try {
      const res = await api.post(API.WITHDRAWAL_ACCEPT(w.id));
      if (res.data?.success === false) {
        showToast(readMessage(res.data, 'Please check this payout and try again.'));
        return;
      }
      showToast(res.data?.message || 'Thank you — payment acknowledged.');
      await loadHistory();
    } catch (err) {
      showToast(readMessage(err.response?.data, 'Please check this payout and try again.'));
    } finally {
      setActionId(null);
    }
  };

  const submitConcern = async () => {
    if (!concernFor) return;
    if ((concernRemark || '').trim().length < 5) {
      showToast('Please describe the concern (min 5 characters).');
      return;
    }
    setActionId(concernFor.id);
    try {
      const res = await api.post(API.WITHDRAWAL_RAISE_CONCERN(concernFor.id), {
        reason: concernReason,
        remark: concernRemark,
      });
      if (res.data?.success === false) {
        showToast(readMessage(res.data, 'Please check your concern details and try again.'));
        return;
      }
      showToast(res.data?.message || 'Concern submitted. Admin will review it.');
      setConcernFor(null);
      setConcernRemark('');
      await loadHistory();
    } catch (err) {
      showToast(readMessage(err.response?.data, 'Please check your concern details and try again.'));
    } finally {
      setActionId(null);
    }
  };

  const submitReopen = async () => {
    if (!reopenFor) return;
    if ((reopenRemark || '').trim().length < 5) {
      showToast('Please describe why you are reopening (min 5 characters).');
      return;
    }
    setActionId(`reopen-${reopenFor.id}`);
    try {
      const res = await api.post(API.WITHDRAWAL_REOPEN_CONCERN(reopenFor.id), {
        description: reopenRemark,
        remark: reopenRemark,
      });
      if (res.data?.success === false) {
        showToast(readMessage(res.data, 'Could not reopen this concern.'));
        return;
      }
      showToast(res.data?.message || 'Concern reopened.');
      setReopenFor(null);
      setReopenRemark('');
      await loadHistory();
    } catch (err) {
      showToast(readMessage(err.response?.data, 'Could not reopen this concern.'));
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10">
      <div className="max-w-2xl mx-auto px-6 space-y-5">
        {toast ? (
          <div className="rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm font-semibold text-violet-800">
            {toast}
          </div>
        ) : null}

        <div className="border-b border-slate-200 pb-5">
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
            Withdrawals
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Monthly pay to your bank / UPI. 1 point = ₹1. You receive the Net amount after TDS.
          </p>
        </div>

        {summary?.open_payout ? (
          <PayoutWalletNote snapshot={summary.open_payout} showWithdrawalsLink={false} />
        ) : null}

        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3.5">
          <p className="text-base font-semibold text-amber-950">TDS 5% is deducted by management</p>
          <p className="text-sm text-amber-900/90 mt-1.5 leading-relaxed">
            On every payout, ICONNECT LLP deducts 5% TDS from your Gross earnings
            and pays only the Net amount to your bank / UPI.
            Example: Gross ₹1,000 → TDS ₹50 → you receive ₹950.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Available', val: balance, accent: true },
            { label: 'Total earned', val: totalEarned },
            { label: 'Total withdrawn', val: totalWithdrawn },
          ].map((s) => (
            <div
              key={s.label}
              className={`rounded-2xl p-4 border ${
                s.accent ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200'
              }`}
            >
              <p className="text-[10px] font-bold uppercase tracking-widest mb-1 text-slate-400">{s.label}</p>
              <p
                className={`text-xl font-black ${s.accent ? '' : 'text-slate-900'}`}
                style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
              >
                ₹{Number(s.val).toLocaleString('en-IN')}
              </p>
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">How payouts work</p>
          <ul className="text-sm text-slate-600 space-y-2 list-disc pl-5">
            <li>You cannot request a withdrawal — ICONNECT LLP generates monthly payouts.</li>
            <li>
              TDS 5% is deducted by ICONNECT LLP from Gross; the amount credited to you is always
              the Net.
            </li>
            <li>After payment, you can view the proof, optionally acknowledge, or raise a concern anytime.</li>
            <li>
              Keep{' '}
              <Link to="/profile" className="font-semibold text-violet-700 underline">
                bank / UPI details
              </Link>{' '}
              updated in Profile.
            </li>
          </ul>
          {!payoutSaved && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
              Add payout details in Profile so management can transfer funds.
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Payout history</p>
          </div>
          {!historyLoading && unpaidCycles.length > 0 ? (
            <div className="mx-5 mt-4 mb-1 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
              <p className="text-xs font-medium uppercase tracking-wide text-emerald-800">
                {unpaidCycles.length > 1
                  ? 'Unpaid months (if paid together)'
                  : 'Unpaid payout'}
              </p>
              <p className="text-lg font-semibold tabular-nums text-emerald-900 mt-1">
                You receive (Net) ₹{formatInr(unpaidConsolidated.net)}
              </p>
              <p className="text-sm text-emerald-900/90 mt-1">
                Gross ₹{formatInr(unpaidConsolidated.gross)}
                {' · '}
                TDS 5% deducted by management ₹{formatInr(unpaidConsolidated.tds)}
                {unpaidCycles.length > 1
                  ? ' (TDS once on total gross)'
                  : ''}
              </p>
            </div>
          ) : null}
          {historyLoading && withdrawals.length === 0 ? (
            <p className="px-5 py-8 text-sm text-slate-400 text-center">Loading…</p>
          ) : withdrawals.length === 0 ? (
            <p className="px-5 py-8 text-sm text-slate-400 text-center">No payouts yet.</p>
          ) : (
            <>
              <ScrollableListBody>
                {withdrawals.map((w) => (
                  <div key={w.id} className="px-5 py-4 border-b border-slate-50 space-y-2">
                    <div className="flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-semibold tabular-nums text-slate-800">
                          {unpaidStatuses.includes(w.status) && unpaidCycles.length > 0
                            ? unpaidCycles.length > 1
                              ? `Month gross ₹${formatInr(cycleGross(w))}`
                              : 'Unpaid month'
                            : `Gross ₹${formatInr(cycleGross(w))}`}
                          <span className="text-slate-500 font-medium">
                            {' '}
                            · ⭐ {Number(w.earned_points ?? w.amount).toLocaleString('en-IN')} pts
                          </span>
                        </p>
                        <p className="text-sm text-slate-600 mt-1">
                          Total Payable ₹{formatInr(w.net_payable_amount ?? w.withdrawal_amount_inr ?? w.amount)}
                          {' · '}Paid ₹{formatInr(w.paid_amount ?? 0)}
                          {' · '}Remaining to Pay ₹{formatInr(w.remaining_amount ?? 0)}
                          {' · '}TDS ₹{formatInr(w.tds_amount ?? 0)}
                        </p>
                        {w.member_confirmation_label ? (
                          <p className="text-xs text-slate-500 mt-0.5">
                            Confirmation: {w.member_confirmation_label}
                          </p>
                        ) : null}
                        <p className="text-xs text-slate-400 mt-0.5">
                          {w.payout_month ? formatAppMonth(w.payout_month) : '—'}
                          {w.cycle_no ? ` · cycle #${w.cycle_no}` : ''}
                          {w.cutoff_at ? ` · counted till ${formatPayoutCutoffDate(w.cutoff_date || w.cutoff_at)}` : ''}
                          {w.paid_at
                            ? ` · paid ${formatAppDate(w.paid_at)}`
                            : ` · ${formatAppDate(w.created_at)}`}
                        </p>
                        {w.admin_note ? (
                          <p className="text-xs text-slate-500 mt-0.5 italic">&quot;{w.admin_note}&quot;</p>
                        ) : null}
                        {(w.earning_allocations || []).length > 0 ? (
                          <div className="mt-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                              Earnings in this payout
                            </p>
                            <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
                              {w.earning_allocations.map((a) => {
                                const line = computeTdsBreakdown(a.gross_points);
                                return (
                                <li key={`${w.id}-${a.earning_month}`}>
                                  {formatAppMonth(a.earning_month)} · Gross ₹{formatInr(a.gross_points)}
                                  {' · '}TDS ₹{formatInr(a.tds_amount ?? line.tds)}
                                  {' · '}Net ₹{formatInr(a.net_payable_amount ?? line.net)}
                                </li>
                                );
                              })}
                            </ul>
                          </div>
                        ) : null}
                        {(w.payment_transactions || []).length > 0 ? (
                          <div className="mt-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                              Payments
                            </p>
                            <ul className="mt-1 space-y-1 text-xs text-slate-600">
                              {w.payment_transactions.map((t) => (
                                <li key={t.id} className="flex flex-wrap items-center gap-2">
                                  <span>
                                    ₹{formatInr(t.amount)} · {t.status}
                                    {t.paid_at ? ` · ${formatAppDate(t.paid_at)}` : ''}
                                  </span>
                                  {t.proof_url ? (
                                    <button
                                      type="button"
                                      className="font-bold text-violet-700 underline"
                                      onClick={() =>
                                        handleMediaView(t.proof_url, {
                                          onPreview: setMedia,
                                          title: 'Payment proof',
                                        })
                                      }
                                    >
                                      View proof
                                    </button>
                                  ) : null}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                        {w.open_concern ? (
                          <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                            <p className="font-semibold">
                              Ticket {w.open_concern.ticket_number} · {w.open_concern.status}
                            </p>
                            <p className="mt-0.5">{w.open_concern.description}</p>
                          </div>
                        ) : null}
                        {(w.concern_tickets || [])
                          .filter((t) => ['resolved', 'rejected'].includes(t.status))
                          .slice(0, 1)
                          .map((t) => (
                            <div
                              key={t.id}
                              className="mt-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600"
                            >
                              <p className="font-semibold text-slate-800">
                                Ticket {t.ticket_number} · {t.status}
                              </p>
                              <AppButton
                                type="button"
                                variant="warning"
                                icon="review"
                                size="sm"
                                className="mt-2"
                                disabled={actionId === `reopen-${t.id}`}
                                onClick={() => {
                                  setReopenFor(t);
                                  setReopenRemark('');
                                }}
                              >
                                Reopen concern
                              </AppButton>
                            </div>
                          ))}
                      </div>
                      <StatusBadge tone={w.status_tone || 'neutral'} uppercase={false}>
                        {w.payment_status_label || w.status_label || w.status}
                      </StatusBadge>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {w.payment_proof_url ? (
                        <button
                          type="button"
                          className="text-[11px] font-bold text-violet-700 underline"
                          onClick={() =>
                            handleMediaView(w.payment_proof_url, {
                              onPreview: setMedia,
                              title: 'Payment proof',
                            })
                          }
                        >
                          View payment proof
                        </button>
                      ) : null}
                      {w.can_accept ? (
                        <AppButton
                          type="button"
                          variant="success"
                          icon="check"
                          size="sm"
                          disabled={actionId === w.id}
                          onClick={() => acceptPayment(w)}
                        >
                          Acknowledge payment
                        </AppButton>
                      ) : null}
                      {w.can_raise_concern && !w.open_concern ? (
                        <AppButton
                          type="button"
                          variant="danger"
                          icon="x"
                          size="sm"
                          disabled={actionId === w.id}
                          onClick={() => {
                            setConcernFor(w);
                            setConcernReason('amount_mismatch');
                            setConcernRemark('');
                          }}
                        >
                          Raise a concern
                        </AppButton>
                      ) : null}
                    </div>
                  </div>
                ))}
              </ScrollableListBody>
              <TablePagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={(n) => {
                  setPageSize(n);
                  setPage(1);
                }}
                count={totalCount}
                rowsPerPageId="member-withdrawals-rows"
                embedded
                className="px-5"
              />
            </>
          )}
        </div>
      </div>

      {concernFor ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
            <h3 className="text-lg font-black text-slate-900">Raise a concern</h3>
            <p className="text-xs text-slate-500">
              Payout #{concernFor.id} · Net ₹{Number(concernFor.net_payable_amount ?? concernFor.withdrawal_amount_inr ?? concernFor.amount).toLocaleString('en-IN')}
            </p>
            <label className="block text-[11px] font-bold text-slate-500 uppercase">Reason</label>
            <select
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              value={concernReason}
              onChange={(e) => setConcernReason(e.target.value)}
            >
              {CONCERN_REASONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <label className="block text-[11px] font-bold text-slate-500 uppercase">Remarks</label>
            <textarea
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              rows={3}
              value={concernRemark}
              onChange={(e) => setConcernRemark(e.target.value)}
              placeholder="Describe the issue…"
            />
            <div className="flex gap-2 pt-2">
              <AppButton
                type="button"
                variant="secondary"
                icon="x"
                className="flex-1"
                onClick={() => setConcernFor(null)}
              >
                Cancel
              </AppButton>
              <AppButton
                type="button"
                variant="danger"
                icon="review"
                className="flex-1"
                disabled={actionId === concernFor.id}
                onClick={submitConcern}
              >
                Submit concern
              </AppButton>
            </div>
          </div>
        </div>
      ) : null}

      {reopenFor ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
            <h3 className="text-lg font-black text-slate-900">Reopen concern</h3>
            <p className="text-xs text-slate-500">
              Ticket {reopenFor.ticket_number}
            </p>
            <textarea
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              rows={3}
              value={reopenRemark}
              onChange={(e) => setReopenRemark(e.target.value)}
              placeholder="What is still unresolved?"
            />
            <div className="flex gap-2 pt-2">
              <AppButton
                type="button"
                variant="secondary"
                icon="x"
                className="flex-1"
                onClick={() => setReopenFor(null)}
              >
                Cancel
              </AppButton>
              <AppButton
                type="button"
                variant="warning"
                icon="review"
                className="flex-1"
                disabled={actionId === `reopen-${reopenFor.id}`}
                onClick={submitReopen}
              >
                Reopen
              </AppButton>
            </div>
          </div>
        </div>
      ) : null}

      <MediaPreviewModal
        open={Boolean(media)}
        src={media?.src}
        title={media?.title}
        subtitle={media?.subtitle}
        onClose={() => setMedia(null)}
      />
    </div>
  );
}

export default Withdrawal;
